// src/components/EditBillModal.tsx
import { useState, useRef, ChangeEvent, MouseEvent } from 'react';
import {
	Modal,
	TextInput,
	Button,
	Group,
	Stack,
	ActionIcon,
	Text,
	Checkbox,
	Box,
	Badge,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { IconTrash, IconPuzzle } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import {
	parseStoredDateAsNative,
	normalizeDateForStorage,
} from '../utils/dateUtils';
import { Bill, BillWidgetData } from '../types';
import WidgetDataModal, { useWidgetIntegrationCount } from './WidgetDataModal';

interface EditBillModalProps {
	opened: boolean;
	onClose: () => void;
	bill: Bill;
	onUpdate: (bill: Bill) => void;
	onDelete: (billId: string) => void;
}

function EditBillModal({
	opened,
	onClose,
	bill,
	onUpdate,
	onDelete,
}: EditBillModalProps) {
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
	const [name, setName] = useState(bill.name);
	const [amount, setAmount] = useState(bill.amount.toFixed(2));
	const [dueDate, setDueDate] = useState<Date | null>(
		parseStoredDateAsNative(bill.dueDate)
	);
	const [isRecurring, setIsRecurring] = useState(bill.recurring || false);
	const [widgetData, setWidgetData] = useState<BillWidgetData>(
		bill.widgetData || {}
	);
	const [widgetModalOpen, setWidgetModalOpen] = useState(false);
	const [dateError, setDateError] = useState('');
	const amountInputRef = useRef<HTMLInputElement>(null);
	const widgetIntegrationCount = useWidgetIntegrationCount();

	const validateDate = (date: Date | null, isRecurring: boolean): string => {
		if (!date) return 'Please select a due date';
		if (isRecurring && date.getDate() > 28) {
			return 'Recurring bills must be due on or before the 28th of the month';
		}
		return '';
	};

	const handleAmountChange = (event: ChangeEvent<HTMLInputElement>) => {
		let numbers = event.target.value.replace(/\D/g, '');
		numbers = numbers.slice(-10);

		if (numbers.length === 0) {
			setAmount('0.00');
			return;
		}

		const decimal = (parseInt(numbers) / 100).toFixed(2);
		setAmount(decimal);

		requestAnimationFrame(() => {
			if (amountInputRef.current) {
				const length = amountInputRef.current.value.length;
				amountInputRef.current.setSelectionRange(length, length);
			}
		});
	};

	const handleDateChange = (date: Date | null) => {
		setDueDate(date);
		if (isRecurring && date) {
			const error = validateDate(date, isRecurring);
			setDateError(error);
			if (error) {
				notifications.show({
					title: 'Invalid Recurring Date',
					message: error,
					color: 'red',
				});
			}
		} else {
			setDateError('');
		}
	};

	const handleRecurringChange = (event: ChangeEvent<HTMLInputElement>) => {
		const newIsRecurring = event.currentTarget.checked;
		setIsRecurring(newIsRecurring);

		// Validate existing date when turning on recurring
		if (newIsRecurring && dueDate) {
			const error = validateDate(dueDate, true);
			setDateError(error);
			if (error) {
				notifications.show({
					title: 'Invalid Recurring Date',
					message:
						'Please select a date on or before the 28th of the month for recurring bills.',
					color: 'red',
				});
				setDueDate(null);
			}
		} else {
			setDateError('');
		}
	};

	const handleSubmit = () => {
		if (!dueDate) {
			setDateError('Please select a due date');
			return;
		}

		if (isRecurring) {
			const error = validateDate(dueDate, true);
			if (error) {
				setDateError(error);
				notifications.show({
					title: 'Invalid Recurring Date',
					message: error,
					color: 'red',
				});
				return;
			}
		}

		if (typeof onUpdate !== 'function') {
			console.error('onUpdate is not a function');
			return;
		}

		const updatedBill: Bill = {
			...bill,
			name: name.trim(),
			amount: parseFloat(amount),
			dueDate: normalizeDateForStorage(dueDate),
			recurring: isRecurring,
		};

		// Handle widgetData: include if has data, remove if empty
		if (Object.keys(widgetData).length > 0) {
			updatedBill.widgetData = widgetData;
		} else {
			delete updatedBill.widgetData;
		}

		onUpdate(updatedBill);
		onClose();
	};

	const handleDelete = () => {
		onDelete(bill.id);
		setShowDeleteConfirm(false);
		onClose();
	};

	const handleAmountClick = (event: MouseEvent<HTMLInputElement>) => {
		const target = event.target as HTMLInputElement;
		const length = target.value.length;
		target.setSelectionRange(length, length);
	};

	if (showDeleteConfirm) {
		return (
			<Modal
				opened={opened}
				onClose={() => setShowDeleteConfirm(false)}
				title='Confirm Deletion'
				centered
			>
				<Stack>
					<Text size='sm'>
						Are you sure you want to delete this bill?
						<br />
						<Text span fw={500}>
							{name} - ${amount}
						</Text>
					</Text>

					<Group justify='flex-end' mt='md'>
						<Button
							variant='light'
							onClick={() => setShowDeleteConfirm(false)}
						>
							Cancel
						</Button>
						<Button color='red' onClick={handleDelete}>
							Delete
						</Button>
					</Group>
				</Stack>
			</Modal>
		);
	}

	return (
		<Modal opened={opened} onClose={onClose} title='Edit Bill' centered>
			<Stack>
				<TextInput
					label='Bill Name'
					placeholder='Enter bill name'
					value={name}
					onChange={(event) => setName(event.currentTarget.value)}
				/>

				<TextInput
					ref={amountInputRef}
					label='Amount'
					placeholder='0.00'
					value={`$${amount}`}
					onChange={handleAmountChange}
					onClick={handleAmountClick}
					styles={{
						input: {
							textAlign: 'right',
							direction: 'ltr',
							fontFeatureSettings: 'tnum',
							caretColor: 'transparent',
						},
					}}
				/>

				<Group align='flex-start' grow>
					<Box style={{ flex: '0 0 25%' }}>
						<DatePickerInput
							clearable
							label='Due Date'
							placeholder='Select'
							value={dueDate}
							onChange={handleDateChange}
							valueFormat='M/D'
							error={dateError}
						/>
					</Box>
					<Box pt={30}>
						<Checkbox
							label='Recurring'
							checked={isRecurring}
							onChange={handleRecurringChange}
							styles={{
								label: {
									whiteSpace: 'nowrap',
								},
							}}
						/>
					</Box>
				</Group>

				<Group justify='space-between' mt='md'>
					<ActionIcon
						variant='light'
						color='red'
						size='lg'
						onClick={() => setShowDeleteConfirm(true)}
					>
						<IconTrash size={20} />
					</ActionIcon>

					<Group>
						{widgetIntegrationCount > 0 && (
							<Button
								variant="light"
								leftSection={<IconPuzzle size={16} />}
								rightSection={
									Object.keys(widgetData).length > 0 ? (
										<Badge size="xs" variant="filled" circle>
											{Object.keys(widgetData).length}
										</Badge>
									) : null
								}
								onClick={() => setWidgetModalOpen(true)}
							>
								Widgets
							</Button>
						)}
						<Button variant='light' onClick={onClose}>
							Cancel
						</Button>
						<Button onClick={handleSubmit}>Save Changes</Button>
					</Group>
				</Group>

				<WidgetDataModal
					opened={widgetModalOpen}
					onClose={() => setWidgetModalOpen(false)}
					existingWidgetData={widgetData}
					onSave={setWidgetData}
					isNewBill={false}
					billName={name}
				/>
			</Stack>
		</Modal>
	);
}

export default EditBillModal;
